// Hand-drawn signature as vector strokes (Owner task book 2026-09-04 Round 2 §C).
//
// The mark is stored as the points the finger drew, in the pad's own pixel space, never as a
// raster: it renders crisply on screen (SVG) and in the agreement PDF, needs no canvas to capture
// (jsdom-safe), and stays a few hundred bytes. `width` / `height` are the pad size at drawing time
// so a later render can scale the mark to any box.
export interface SignatureStrokes {
  width: number;
  height: number;
  // One entry per stroke: [x0, y0, x1, y1, …] in pad pixels.
  strokes: number[][];
}

export const SIGNATURE_MIN_INK = 24;

export function emptySignature(width: number, height: number): SignatureStrokes {
  return { width: Math.max(1, Math.round(width)), height: Math.max(1, Math.round(height)), strokes: [] };
}

// A mark counts only when the finger actually travelled: a single dot is not a signature.
export function signatureInk(signature: SignatureStrokes | null): number {
  if (!signature) return 0;
  let ink = 0;
  for (const stroke of signature.strokes) {
    for (let i = 2; i + 1 < stroke.length; i += 2) ink += Math.hypot(stroke[i] - stroke[i - 2], stroke[i + 1] - stroke[i - 1]);
  }
  return ink;
}

export function signatureIsMeaningful(signature: SignatureStrokes | null): boolean {
  return signatureInk(signature) >= SIGNATURE_MIN_INK;
}

export function withStroke(signature: SignatureStrokes, stroke: number[]): SignatureStrokes {
  if (stroke.length < 2) return signature;
  return { ...signature, strokes: [...signature.strokes, stroke] };
}

function fmt(n: number): string {
  return String(Math.round(n * 10) / 10);
}

// SVG path data of the whole mark (pad coordinates); the caller sets the viewBox to width/height.
export function signaturePath(signature: SignatureStrokes): string {
  const parts: string[] = [];
  for (const stroke of signature.strokes) {
    if (stroke.length < 2) continue;
    if (stroke.length === 2) {
      parts.push(`M${fmt(stroke[0])} ${fmt(stroke[1])} l0.1 0`);
      continue;
    }
    let d = `M${fmt(stroke[0])} ${fmt(stroke[1])}`;
    for (let i = 2; i + 1 < stroke.length; i += 2) d += ` L${fmt(stroke[i])} ${fmt(stroke[i + 1])}`;
    parts.push(d);
  }
  return parts.join(' ');
}

// Inline SVG markup of the mark (used by the agreement document render and the read-only pad).
export function signatureSvg(signature: SignatureStrokes, options: { width?: number; height?: number; stroke?: string; className?: string } = {}): string {
  const width = options.width ?? signature.width;
  const height = options.height ?? signature.height;
  const cls = options.className ? ` class="${options.className}"` : '';
  return `<svg${cls} viewBox="0 0 ${signature.width} ${signature.height}" width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="signature"><path d="${signaturePath(signature)}" fill="none" stroke="${options.stroke ?? '#0b1c30'}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}

export function isSignatureStrokes(value: unknown): value is SignatureStrokes {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  if (typeof v.width !== 'number' || typeof v.height !== 'number' || !Array.isArray(v.strokes)) return false;
  return v.strokes.every((s) => Array.isArray(s) && s.every((n) => typeof n === 'number' && Number.isFinite(n)));
}
